import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';

import stripNonUnicode from 'utils/stripNonUnicode';
import getPatientAddress from 'utils/print/getPatientAddress';
import getPatientName from 'utils/print/getPatientName';
import getPatientCity from 'utils/print/getPatientCity';
import getPatientZip from 'utils/print/getPatientZip';
import getPatientFullPhone from 'utils/print/getPatientFullPhone';
import getPatientDeliveryNotes from 'utils/print/getPatientDeliveryNotes';

import dateFormat from 'config/dateFormat';
import timeFormat from 'config/timeFormat';
import timeDateFormat from 'config/timeDateFormat';

const scheduleToPDF = ({
  inputPDF,
  schedule,
  values = {},
}) => new Promise((resolve) => {
  const lineHeight = 1.2;
  const margin = 10;
  const ptsPerMm = 2.834646;
  const fontSize = 10;
  const titleFontSize = 16;
  const fontType = 'normal';
  const textColor = 0;
  const drawColor = 0;
  const lineHeightMm = fontSize * (lineHeight / ptsPerMm);
  const format = 'a4';
  // eslint-disable-next-line new-cap
  const doc = inputPDF || new jsPDF({
    orientation: 'l',
    unit: 'mm',
    format,
    lineHeight,
  });
  if (inputPDF) {
    doc.addPage(format, 'l');
  }
  doc.setFont('times');
  let y = margin;
  let x = margin;
  const pageContentWidth = doc.internal.pageSize.getWidth() - (margin * 2);
  let maxWidth = pageContentWidth;
  const setDefaults = () => {
    doc.setFontSize(fontSize);
    doc.setFontType(fontType);
    doc.setTextColor(textColor);
    doc.setDrawColor(drawColor);
  };
  const addY = (newY) => {
    if ((y + newY) < doc.internal.pageSize.getHeight() - (margin * 2)) {
      y += newY;
    } else {
      doc.addPage();
      y = margin;
    }
  };
  const renderTitle = () => {
    doc.setFontSize(titleFontSize);
    doc.setFontType('bold');
    x = (doc.internal.pageSize.getWidth() / 2);
    maxWidth = pageContentWidth * 0.75;
    const driver = schedule[0]['Order Line'][0].Driver;
    const dayOfDelivery = moment(schedule[0]['Order Line'][0]['Date of Delivery'], timeDateFormat)
      .format(dateFormat);
    const defaultTitle = driver.ID
      ? `${driver['First Name']} ${driver['Last Name']}, ${dayOfDelivery}`
      : `Driver Not Assigned, ${dayOfDelivery}`;
    const title = values && values.Name ? values.Name : defaultTitle;
    const text = doc.splitTextToSize(title, maxWidth);
    text.forEach((line) => {
      addY(titleFontSize * (lineHeight / ptsPerMm));
      doc.text(line, x, y, null, null, 'center');
    });
    addY(margin / 4);
  };
  const renderDeliveries = () => {
    setDefaults();
    addY(lineHeightMm);
    x = margin;
    maxWidth = pageContentWidth;
    const columns = [
      { title: 'Time', dataKey: 'timeOfDelivery' },
      { title: 'Patient Name', dataKey: 'patientName' },
      { title: 'Address', dataKey: 'address' },
      { title: 'City', dataKey: 'city' },
      { title: 'Zip', dataKey: 'zip' },
      { title: 'Phone', dataKey: 'phone' },
      { title: 'Products', dataKey: 'products' },
      { title: 'Comments', dataKey: 'comments' },
    ];
    const rows = schedule.map((item) => {
      const valuesItem = values && values[`schedule-${item.ID}`]
        ? values[`schedule-${item.ID}`] : null;
      if (valuesItem) {
        return valuesItem;
      }
      return {
        timeOfDelivery: moment(item['Order Line'][0]['Date of Delivery'], timeDateFormat)
          .format(timeFormat),
        patientName: stripNonUnicode(getPatientName({ patient: item.Patient })),
        address: getPatientAddress({ patient: item.Patient }) || '',
        city: getPatientCity({ patient: item.Patient }) || '',
        zip: getPatientZip({ patient: item.Patient }) || '',
        phone: getPatientFullPhone({ patient: item.Patient }) || '',
        products: item['Order Line']
          .map(orderLine => (
            orderLine.Product['Product Name']
              ? stripNonUnicode(`${orderLine.Product['Product Name']} ${orderLine.Product.Model || ''} x${orderLine.Units};`)
              : stripNonUnicode(`${orderLine.Description} x${orderLine.Units};`)))
          .join('\n'),
        comments: stripNonUnicode(getPatientDeliveryNotes({ patient: item.Patient })) || '',
      };
    });
    doc.autoTable(columns, rows, {
      theme: 'grid',
      startY: y,
      showHeader: 'everyPage',
      margin: [0, 0, 0, x],
      tableWidth: maxWidth,
      styles: {
        font: 'times',
        textColor,
        lineColor: drawColor,
        fontSize,
        cellPadding: [margin / 8, margin / 4, margin / 8, margin / 4],
        columnWidth: 'wrap',
        overflow: 'linebreak',
      },
      headerStyles: {
        lineWidth: 0.1,
        lineColor: drawColor,
        fillColor: 'white',
      },
      columnStyles: {
        patientName: {
          columnWidth: 40,
        },
        address: {
          columnWidth: 30,
        },
        zip: {
          columnWidth: 14,
        },
        products: {
          columnWidth: 'auto',
        },
        comments: {
          columnWidth: 50,
        },
      },
    });
    const table = doc.autoTable.previous;
    addY((table.finalY - y) + margin);
  };

  renderTitle();
  renderDeliveries();
  resolve(doc);
});

export default scheduleToPDF;



// WEBPACK FOOTER //
// ./src/utils/scheduleToPDF.js